import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Close } from '@mui/icons-material';

import { CATEGORIES, UPDATE_LIMITS, UPLOAD_DISABLED_TOOLTIP, VISUAL_TYPES } from './constants';
import RichTextField from './RichTextField';
import { UpdateFormErrors, WhatsNewUpdate } from './types';
import {
  buildUpdateId,
  hasErrors,
  isoToLocalInput,
  localInputToIso,
  toNullable,
  validateUpdate
} from './utils';

interface UpdateDialogProps {
  open: boolean;
  /** `null` opens the dialog for a new update. */
  update: WhatsNewUpdate | null;
  /** Ids already in the list, so a new update cannot collide with an existing one. */
  existingIds: string[];
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars -- parameter name documents the typed callback
  onSave: (update: WhatsNewUpdate) => void;
}

const buildBlankUpdate = (): WhatsNewUpdate => ({
  id: '',
  title: '',
  description: '',
  category: null,
  content: null,
  visual_type: null,
  visual_url: null,
  is_published: false,
  published_at: new Date().toISOString(),
  expires_at: null,
  cta_label: null,
  cta_url: null
});

const UpdateDialog: React.FC<UpdateDialogProps> = ({
  open,
  update,
  existingIds,
  onClose,
  onSave
}) => {
  const [form, setForm] = useState<WhatsNewUpdate>(buildBlankUpdate);
  const [errors, setErrors] = useState<UpdateFormErrors>({});
  const [idEdited, setIdEdited] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setForm(update ? { ...update } : buildBlankUpdate());
    setErrors({});
    setIdEdited(false);
  }, [open, update]);

  const isEditing = Boolean(update);

  /**
   * New updates get an id derived from the title and publish month, matching the
   * ids already in the stored config. Existing ids are left alone, since changing
   * one is a delete plus a create as far as any consumer is concerned.
   */
  useEffect(() => {
    if (isEditing || idEdited) {
      return;
    }
    setForm(previous => ({
      ...previous,
      id: buildUpdateId(previous.title, previous.published_at)
    }));
  }, [form.title, form.published_at, isEditing, idEdited]);

  const setField = <K extends keyof WhatsNewUpdate>(field: K, value: WhatsNewUpdate[K]) => {
    setForm(previous => ({ ...previous, [field]: value }));
  };

  const handleSave = () => {
    const normalised: WhatsNewUpdate = {
      ...form,
      title: form.title.trim(),
      description: form.description.trim(),
      category: toNullable(form.category),
      content: toNullable(form.content),
      visual_type: toNullable(form.visual_type),
      visual_url: toNullable(form.visual_url),
      cta_label: toNullable(form.cta_label),
      cta_url: toNullable(form.cta_url)
    };

    const validationErrors = validateUpdate(normalised, isEditing ? [] : existingIds);
    setErrors(validationErrors);

    if (hasErrors(validationErrors)) {
      return;
    }

    onSave(normalised);
  };

  const publishedAtInput = useMemo(() => isoToLocalInput(form.published_at), [form.published_at]);
  const expiresAtInput = useMemo(() => isoToLocalInput(form.expires_at), [form.expires_at]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant='h6' component='span'>
          {isEditing ? 'Edit update' : 'Add update'}
        </Typography>
        <IconButton onClick={onClose} size='small' aria-label='Close'>
          <Close fontSize='small' />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label='Title'
              required
              fullWidth
              size='small'
              value={form.title}
              error={Boolean(errors.title)}
              helperText={errors.title ?? `${form.title.length}/${UPDATE_LIMITS.title}`}
              inputProps={{ maxLength: UPDATE_LIMITS.title }}
              onChange={event => setField('title', event.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label='Description'
              required
              fullWidth
              multiline
              minRows={3}
              size='small'
              value={form.description}
              error={Boolean(errors.description)}
              helperText={
                errors.description ?? `${form.description.length}/${UPDATE_LIMITS.description}`
              }
              inputProps={{ maxLength: UPDATE_LIMITS.description }}
              onChange={event => setField('description', event.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label='Category'
              fullWidth
              size='small'
              value={form.category ?? ''}
              error={Boolean(errors.category)}
              helperText={errors.category ?? ' '}
              onChange={event => setField('category', event.target.value)}>
              <MenuItem value=''>None</MenuItem>
              {CATEGORIES.map(category => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_published}
                    onChange={event => setField('is_published', event.target.checked)}
                  />
                }
                label='Published'
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <RichTextField
              label='Content'
              value={form.content ?? ''}
              error={Boolean(errors.content)}
              helperText={
                errors.content ?? `${(form.content ?? '').length}/${UPDATE_LIMITS.content}`
              }
              onChange={html => setField('content', html)}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              select
              label='Visual type'
              fullWidth
              size='small'
              value={form.visual_type ?? ''}
              error={Boolean(errors.visual_type)}
              helperText={errors.visual_type ?? ' '}
              onChange={event => setField('visual_type', event.target.value)}>
              <MenuItem value=''>None</MenuItem>
              {VISUAL_TYPES.map(type => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label='Visual URL'
              fullWidth
              size='small'
              value={form.visual_url ?? ''}
              error={Boolean(errors.visual_url)}
              helperText={errors.visual_url ?? ' '}
              inputProps={{ maxLength: UPDATE_LIMITS.visualUrl }}
              onChange={event => setField('visual_url', event.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <Tooltip title={UPLOAD_DISABLED_TOOLTIP}>
              <Box>
                <Button variant='outlined' fullWidth disabled sx={{ height: 40 }}>
                  Upload
                </Button>
              </Box>
            </Tooltip>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label='Published at'
              type='datetime-local'
              required
              fullWidth
              size='small'
              value={publishedAtInput}
              error={Boolean(errors.published_at)}
              helperText={errors.published_at ?? ' '}
              InputLabelProps={{ shrink: true }}
              onChange={event =>
                setField('published_at', localInputToIso(event.target.value) ?? '')
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label='Expires at'
              type='datetime-local'
              fullWidth
              size='small'
              value={expiresAtInput}
              error={Boolean(errors.expires_at)}
              helperText={errors.expires_at ?? 'Optional. Must be later than published at.'}
              InputLabelProps={{ shrink: true }}
              onChange={event => setField('expires_at', localInputToIso(event.target.value))}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label='CTA label'
              fullWidth
              size='small'
              value={form.cta_label ?? ''}
              error={Boolean(errors.cta_label)}
              helperText={errors.cta_label ?? ' '}
              inputProps={{ maxLength: UPDATE_LIMITS.ctaLabel }}
              onChange={event => setField('cta_label', event.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label='CTA URL'
              fullWidth
              size='small'
              placeholder='https://'
              value={form.cta_url ?? ''}
              error={Boolean(errors.cta_url)}
              helperText={errors.cta_url ?? ' '}
              onChange={event => setField('cta_url', event.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label='ID'
              required
              fullWidth
              size='small'
              value={form.id}
              disabled={isEditing}
              error={Boolean(errors.id)}
              helperText={
                errors.id ??
                (isEditing
                  ? 'Ids cannot be changed once saved.'
                  : 'Generated from the title and publish month. Edit if you need a different one.')
              }
              inputProps={{ maxLength: UPDATE_LIMITS.id }}
              onChange={event => {
                setIdEdited(true);
                setField('id', event.target.value);
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={handleSave}>
          {isEditing ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateDialog;
